export const enSettings = {
  title: "Settings",
  subtitle: "Manage tenant configuration and integrations.",
  noSectionsAvailable: "You do not have access to any settings sections.",
  tabs: {
    aria: "Settings sections",
    sso: "SSO",
    businessContext: "Business Context",
    llm: "LLM",
    tokenUsage: "Token Usage",
    logs: "Logs",
    branding: "Branding",
  },
  logs: {
    loading: "Loading logs",
    empty: "No API activity recorded for this period.",
    anonymous: "Not signed in",
    // A user who has since been deleted, or whose names could not be loaded for want of
    // user-view. The id is still worth showing: it is what the row actually recorded.
    unknownUser: "User {{id}}",
    filters: {
      user: "User ID",
      userAny: "Any user",
      event: "Event",
      outcome: "Outcome",
      outcomeAny: "Any",
      outcomeSucceeded: "Succeeded",
      outcomeFailed: "Failed",
      start: "From",
      end: "To",
      apply: "Apply",
      reset: "Reset",
    },
    columns: {
      time: "Time",
      user: "User",
      event: "Event",
      method: "Method",
      path: "Path",
      status: "Status",
      duration: "Duration",
    },
    pagination: {
      range: "{{from}}-{{to}} of {{total}}",
      previous: "Previous",
      next: "Next",
    },
    errors: {
      loadFailed: "Could not load the logs.",
    },
  },
  tokenUsage: {
    loading: "Loading token usage",
    description:
      "Total LLM tokens this tenant has used, across every feature. Figures are all-time and cover both Olive's keys and any of your own.",
    empty: "No token usage recorded yet.",
    tokensHeader: "Total tokens",
    inputHeader: "Input tokens",
    // "of which", not a fourth kind of spend: these tokens are already counted in the input
    // column, and the provider bills them at a discount rather than not at all.
    cachedHeader: "Cached input",
    outputHeader: "Output tokens",
    totals: {
      total: "Total tokens",
      input: "Input tokens",
      cached: "Cached input",
      output: "Output tokens",
      calls: "Provider calls",
    },
    byFeature: {
      title: "By feature",
      nameHeader: "Feature",
      countHeader: "Runs",
      // The drilldown's count column switches meaning to provider calls, since one run can use
      // several models. Named in the aria labels so a screen reader is not left guessing.
      showModels: "Show models for {{name}}",
      hideModels: "Hide models for {{name}}",
    },
    groups: {
      // "viewing" vs "building" rather than one "Apps" line: these two costs scale with different
      // things, and that difference is the reason the grouping exists.
      publishedApps: "Published apps — viewing",
      appStudio: "App Studio — building",
      chat: "Chat",
      semanticModel: "Semantic model",
      nuggets: "Nuggets",
      classification: "Classification",
    },
    byModel: {
      title: "By model",
      nameHeader: "Model",
      // Calls, not runs: one run can use several models, so these do not sum to the run count.
      countHeader: "Calls",
    },
    features: {
      chatTurn: "Chats",
      nuggetGraphRun: "Nugget runs",
      semanticModelGeneration: "Semantic model generation",
      semanticCandidates: "Semantic candidates",
      chatSummary: "Chat summaries",
      learningInsights: "Learning insights",
      domainLearningInsights: "Domain learnings",
      appItemSummary: "App AI summaries",
      appQueryNaming: "Query naming",
      appChartDesign: "Chart design",
      appWidgetDraft: "App Studio widget drafts",
      semanticInterview: "Semantic interview",
      nuggetActionImage: "Nugget action images",
      classificationRun: "Classification runs",
      classificationExamples: "Classification examples",
    },
    errors: {
      loadFailed: "Could not load token usage.",
    },
  },
  llm: {
    loading: "Loading LLM API keys",
    description:
      "Add your own OpenAI or Anthropic API keys. Requests for the models you select will use your key, and all other requests continue to use Olive's keys.",
    empty: "No API keys configured. All requests currently use Olive's keys.",
    addTitle: "Add an API key",
    editTitle: "Edit API key",
    configured: "Configured",
    providers: {
      openai: "OpenAI",
      anthropic: "Anthropic",
    },
    modelKinds: {
      chat: "Chat",
      embedding: "Embeddings",
      image: "Image",
    },
    fields: {
      provider: "Provider",
      name: "Name",
      models: "Models this key may serve",
      apiKey: "API key",
      apiKeyReplace: "Replace API key",
    },
    placeholders: {
      name: "Production",
      models: "Select models",
      apiKeyEdit: "Leave blank to keep the current key",
    },
    helpers: {
      name: "A label to identify this key.",
      models: "The same model may be attached to more than one key.",
      apiKey: "Stored encrypted and never displayed again after saving.",
      apiKeyEdit:
        "Leave blank to keep the current key, or enter a new key to replace it.",
      providerLocked:
        "The provider cannot be changed. Delete this key and add a new one instead.",
    },
    modal: {
      closeAria: "Close",
      cancel: "Cancel",
    },
    actions: {
      // Distinct from `save` on purpose: the trigger stays in the DOM behind the open dialog, so a
      // shared label would leave two identically named buttons on the page.
      newKey: "New key",
      save: "Add key",
      saveChanges: "Save changes",
      saveSucceeded: "API key saved.",
      updateSucceeded: "API key updated.",
      edit: "Edit API key",
      delete: "Delete API key",
    },
    delete: {
      title: "Delete this API key?",
      body: '"{{name}}" will be removed. Any model it serves falls back to Olive\'s own key.',
      cancel: "Cancel",
      confirm: "Delete",
    },
    errors: {
      loadFailed: "Could not load LLM API keys.",
      saveFailed: "Could not save the API key.",
      updateFailed: "Could not update the API key.",
      deleteFailed: "Could not delete the API key.",
    },
  },
  sso: {
    loading: "Loading SSO configuration",
    sections: {
      samlConfiguration: "SAML configuration",
      identityProvider: "Enter the information from your Identity provider",
      userAttributes: "User attributes:",
      metadata: "Metadata",
      configurations: "SSO configurations",
    },
    fields: {
      name: "Name",
      entityId: "Identifier (Entity ID)",
      replyUrl: "Reply URL (Assertion Consumer Service URL)",
      givenNameAttribute: "Given name (e.g. https://schemas…/givenname)",
      emailAttribute: "Email (e.g. https://schemas…/email address)",
      roleAssignmentAttribute:
        "Role assignment attribute — optional (e.g. https://schemas…/department)",
      metadataUrl: "URL",
    },
    placeholders: {
      name: "Type name here",
      attributeUrl: "Copy paste here",
      metadataUrl: "https://…",
    },
    helpers: {
      roleAssignmentAttribute:
        "Any claim your identity provider sends — department, groups, job title. Its value is matched against the rules below to grant domain access automatically. Leave blank if you assign access manually.",
    },
    state: {
      configured: "Configured",
      enabled: "Enabled",
    },
    copyAria: "Copy to clipboard",
    copiedAria: "Copied",
    actions: {
      create: "Create",
      createSucceeded: "SSO configuration created.",
      enable: "Enable SSO",
      editAttributes: "Edit user attributes",
      // Distinct from editAttributes: when the role-assignment slot is still empty this is an
      // additive step, and calling it "edit" made admins read the missing field as a broken one.
      addRoleAssignmentAttribute: "Add user attribute",
      cancelEditAttributes: "Cancel",
      saveAttributes: "Save attributes",
      saveAttributesSucceeded: "User attributes updated.",
    },
    roleAssignmentRules: {
      sectionTitle: "Automatic domain access",
      description:
        "When someone signs in through SSO, the value of the role assignment attribute is matched against these rules and any matching domain access is granted. Add several rules with the same value to grant access to several domains at once.",
      // Names the button explicitly: the first person through this flow read the gate as a missing
      // feature rather than a step they had not done yet.
      attributeMissing:
        'No role assignment attribute is set yet, so there is nothing to match rules against. Choose "Add user attribute" above, paste the claim your identity provider sends (department, groups, job title — whatever you use), and save it. The rules table appears here once it is set.',
      // Shown before changing which claim feeds the slot: rule values came from the old claim, so
      // they may quietly stop matching -- and a rule that stops matching now *withdraws* the access
      // it granted, rather than just failing to grant it again. Deliberately does not offer to
      // delete the rules: a value can still be valid under the new claim, and we cannot tell which.
      attributeChange: {
        heading: "Change the role assignment attribute?",
        // Leads with the loss, and says when: "next time they use Olive" rather than "next
        // sign-in", because reconciliation runs per request and an open session is not spared.
        subheading_one:
          "1 automatic access rule matches values from the current attribute. If it no longer matches after this change, everyone it granted access to loses that access the next time they use Olive — an open session is not spared. Check your rule against the new attribute straight away.",
        subheading_other:
          "{{count}} automatic access rules match values from the current attribute. Any that no longer match after this change will withdraw the access they granted, from everyone holding it, the next time those people use Olive — an open session is not spared. Check your rules against the new attribute straight away.",
        // Clearing is the sharpest case and deserves its own words: with no attribute there is no
        // value to match, so *every* rule stops matching at once and the loss is certain rather
        // than possible.
        clearingHeading: "Remove the role assignment attribute?",
        clearing_one:
          "Removing the attribute leaves nothing for your 1 automatic access rule to match, so everyone who was granted access by it loses that access the next time they use Olive. The rule is kept, and access granted by hand is unaffected.",
        clearing_other:
          "Removing the attribute leaves nothing for your {{count}} automatic access rules to match, so everyone who was granted access by them loses that access the next time they use Olive. The rules are kept, and access granted by hand is unaffected.",
        clearingConfirm: "Remove attribute",
        cancel: "Cancel",
        confirm: "Change attribute",
      },
      addRule: "Add rule",
      empty:
        "No rules yet. Users signing in through SSO get no domain access automatically.",
      deleteAria: "Delete rule for {{value}} on {{domain}} as {{role}}",
      table: {
        ariaLabel: "Automatic domain access rules",
        attributeValue: "Attribute value",
        domain: "Domain",
        role: "Role",
        actions: "Actions",
      },
      newRuleModal: {
        title: "Add an automatic access rule",
        closeAria: "Close",
        fields: {
          attributeValue: "Attribute value",
          domain: "Domain",
          role: "Role",
        },
        placeholders: {
          attributeValue: "e.g. sales",
        },
        helpers: {
          attributeValue:
            "Must match the value your identity provider sends. Capitalisation does not matter. Pick an existing value to add another domain to it.",
        },
        validation: {
          required: "This field is required.",
        },
        actions: {
          cancel: "Cancel",
          addRule: "Add rule",
        },
        errors: {
          saveFailed: "Could not add the rule.",
        },
      },
      deleteDialog: {
        heading: "Delete this rule?",
        // The consequential half is that this takes access away, so it leads.
        subheading:
          'Anyone who currently has {{role}} on {{domain}} because their attribute is "{{value}}" will lose it the next time they use Olive. Nobody signing in through your identity provider will be granted it again.',
        cancel: "Cancel",
        confirm: "Delete",
      },
      errors: {
        loadFailed: "Could not load the automatic access rules.",
        deleteFailed: "Could not delete the rule.",
      },
    },
    validation: {
      required: "This field is required.",
      invalidUrl: "Enter a valid URL.",
      maxLength: "Must be 512 characters or fewer.",
      nameRequired: "Name is required",
      nameMaxLength: "Name must be 32 characters or fewer",
      nameInvalidCharacters: "Name must not contain spaces or underscores",
    },
    errors: {
      loadFailed: "Unable to load SSO configurations.",
      createFailed: "Unable to create SSO configuration.",
      enableFailed: "Unable to enable SSO.",
      saveAttributesFailed: "Unable to update the user attributes.",
    },
    cognito: {
      loading: "Loading SAML configuration…",
      unavailable: "SAML configuration unavailable.",
    },
    credentialLogin: {
      sectionTitle: "Sign-in methods",
      enabled:
        "Users can sign in with their email and password, or through your identity provider.",
      disabled:
        "Email and password sign-in is turned off. Users sign in through your identity provider only.",
      action: "Turn off email and password sign-in",
      confirm: {
        title: "Turn off email and password sign-in?",
        body: "Everyone will sign in through your identity provider from now on. Confirm that signing in through it works before you continue — if it does not, no one will be able to get in. This cannot be undone from Settings.",
        cancel: "Cancel",
        submit: "Turn off",
      },
      errors: {
        disableFailed: "Unable to turn off email and password sign-in.",
      },
    },
  },
  businessContext: {
    loading: "Loading business context",
    fields: {
      industry: "Industry",
      companyOverview: "Company overview",
      companyHistory: "Company history",
    },
    placeholders: {
      industry: "e.g. Video games / interactive entertainment",
      companyOverview: "What the company does, product lines, business model",
      companyHistory: "Founding story, pivots, acquisitions",
    },
    actions: {
      save: "Save",
      saveSucceeded: "Business context saved.",
    },
    errors: {
      loadFailed: "Unable to load business context.",
      saveFailed: "Unable to save business context.",
    },
  },
  branding: {
    loading: "Loading branding",
    sections: {
      aria: "Branding sections",
      logo: "Logo",
      colors: "Colors",
    },
    description:
      "Replace Olive's icon and name with your own. Everyone in this workspace sees them in the sidebar, and the icon in the browser tab.",
    preview: {
      title: "Preview",
      iconWithName: "Icon with name",
      iconWithNameWhere: "Expanded sidebar",
      icon: "Icon",
      iconWhere: "Collapsed sidebar and browser tab",
      iconAlt: "Icon preview",
      fullLogoAlt: "Icon with name preview",
      fullLogoMissing: "Upload an image to preview",
    },
    icon: {
      label: "Icon",
      hint: "A square image, shown on its own in the collapsed sidebar and the browser tab. PNG, JPEG, WebP or SVG, up to {{maxKb}} KB.",
      upload: "Upload icon",
      replace: "Replace icon",
      remove: "Remove icon",
    },
    iconWithName: {
      label: "Icon with name",
      hint: "What the expanded sidebar shows: your full logo as one image, or your icon beside a name.",
      styleAria: "Icon with name style",
      image: "Image",
      name: "Name",
    },
    fullLogo: {
      hint: "Your full logo, icon and name together, shown on its own in the expanded sidebar. Use a transparent background so it sits on the sidebar's color. PNG, JPEG, WebP or SVG, up to {{maxKb}} KB.",
      upload: "Upload icon with name",
      replace: "Replace icon with name",
      required: "Upload an image, or switch to Name.",
    },
    name: {
      label: "Brand name",
      placeholder: "Olive",
      hint: "Shown beside the icon in the expanded sidebar. Leave blank to use Olive.",
    },
    actions: {
      save: "Save",
      reset: "Reset to default",
      saveSucceeded: "Branding saved.",
      resetSucceeded: "Logo and name reset to Olive's default.",
    },
    errors: {
      loadFailed: "Unable to load branding.",
      saveFailed: "Unable to save branding.",
      resetFailed: "Unable to reset branding.",
      imageTooLarge: "The image must be {{maxKb}} KB or smaller.",
      imageTypeNotAllowed: "The image must be a PNG, JPEG, WebP or SVG file.",
    },
  },
  brandColors: {
    description:
      "Pick your two brand colors. Every other shade is worked out from them, for light and dark mode — hover states, the sidebar, dialog headers — and adjusted where needed so text stays readable.",
    usingDefault: "Using Olive's default colors.",
    primary: {
      label: "Primary color",
      hint: "Buttons, links, the selected tab and menu item.",
      pickerAria: "Pick the primary color",
    },
    secondary: {
      label: "Secondary color",
      hint: "The sidebar and dialog headers, outlines and chart accents.",
      pickerAria: "Pick the secondary color",
    },
    adjustedNote:
      "To keep white text readable on buttons, the light theme uses a darker shade of your primary color: {{color}}.",
    preview: {
      title: "Preview",
      light: "Light",
      dark: "Dark",
      navHome: "Home",
      navChats: "Chats",
      navApps: "Apps",
      tabOverview: "Overview",
      tabDetails: "Details",
      cardTitle: "Monthly revenue",
      cardLink: "View report",
      button: "Save",
      chip: "Selected",
    },
    swatches: {
      primary: "Primary",
      hover: "Hover",
      light: "Light",
      accent: "Accent",
      sidebar: "Sidebar",
    },
    actions: {
      save: "Save",
      reset: "Reset to default",
      saveSucceeded: "Brand colors saved.",
      resetSucceeded: "Colors reset to Olive's default.",
    },
    errors: {
      saveFailed: "Unable to save brand colors.",
      resetFailed: "Unable to reset brand colors.",
    },
  },
} as const;
